import { Button, message, Modal } from 'antd';
import { CheckboxButton, CloseIcon, Subtitle } from '../shared';
import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { ILineResource } from '../../entities/farms.entities';
import { useState, useEffect } from 'react';
import { sendSingleRequest } from '../../apis';
import { loadFarmsData } from '../../store/farms/farms.actions';
import { SelectTagsByFarmID } from '../../store/extra/extra.selector';
import { periodToStr } from '../../lib/common.helpers';
import { loadTags } from '../../store/automation/automation.actions';
import { loadExtraData } from '../../store/extra/extra.actions';
import PastTagModal from './PastTagModal';
import TagDataModal from './TagDataModal';

interface Props {
  lineData: ILineResource;
  visible: boolean;
  onClose: () => void;
  onComplete?: () => void;
  onChange?: () => void;
}

const LineTagModal = ({
  visible,
  onClose,
  onComplete,
  onChange,
  lineData,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const lineTags = SelectTagsByFarmID(lineData.farm_id);

  const [disabled, setDisabled] = useState(false);
  const [isPast, setIsPast] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Set<number>>(new Set());
  const [initialTags, setInitialTags] = useState<Set<number>>(new Set());
  const [showConfirmation, setShowConfirmation] = useState(false);

  const toggleUrgent = async (is_urgent: boolean) => {
    if (lineData.is_urgent === is_urgent) return;
    setDisabled(true);
    const response = await sendSingleRequest(
      { line_id: lineData.id, is_urgent },
      'PUT',
      `api/farm/line/lines/${lineData.id}`,
      true,
    );
    if (response.status) {
      await dispatch(loadFarmsData());
      if (onChange) {
        await onChange();
      }
      dispatch(loadExtraData());
      setDisabled(false);
    } else {
      setDisabled(false);
      message.error(translate(lang, response.data?.message ?? 'Unknown error'));
    }
  };
  const toggleTag = (tagId: number) => {
    setSelectedTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  };

  const hasChanges = () => {
    if (selectedTags.size !== initialTags.size) return true;
    for (const tagId of selectedTags) {
      if (!initialTags.has(tagId)) return true;
    }
    return false;
  };

  const handleConfirm = () => {
    if (!hasChanges()) {
      onClose();
      return;
    }
    setShowConfirmation(true);
  };

  const handleCancel = () => {
    if (showConfirmation) {
      setShowConfirmation(false);
    } else {
      onClose();
    }
  };

  const applyChanges = async (should_create_task: boolean) => {
    setDisabled(true);

    const tagIDs = Array.from(selectedTags);
    const response = await sendSingleRequest(
      { line_id: lineData.id, tags: tagIDs, should_create_task },
      'POST',
      'api/tag-line',
      true,
    );
    if (!response.status) {
      message.error(translate(lang, response.data?.message ?? 'Unknown error'));
      setDisabled(false);
      return;
    }

    await dispatch(loadTags());
    if (onChange) {
      await onChange();
    }
    await dispatch(loadExtraData());
    await dispatch(loadFarmsData());

    onClose();
  };

  const handleYes = async () => {
    await applyChanges(true);
  };
  const handleNo = async () => {
    await applyChanges(false);
  };

  useEffect(() => {
    if (visible) {
      const currentTags = new Set(
        lineTags
          ?.filter(tag => tag.tag_lines.some(tl => tl.line_id === lineData.id))
          .map(tag => tag.id) ?? [],
      );
      setSelectedTags(currentTags);
      setInitialTags(new Set(currentTags));
    }
  }, [visible, lineData.id, lineTags]);

  return isPast ? (
    <PastTagModal
      visible={true}
      onClose={onClose}
      lineData={lineData}
      onChange={onChange}
      onComplete={onComplete}
    />
  ) : addNew ? (
    <TagDataModal onClose={() => setAddNew(false)} />
  ) : showConfirmation ? (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={550}
    >
      <div className='wrap text-center'>
        <div className='mb-32 mt-32'>
          <Subtitle color='black-1' align='center' size={1} fontWeight={600}>
            {translate(lang, 'Do you want to create tasks for tagging lines?')}
          </Subtitle>
        </div>
        <div className='d-flex justify-center gap-22 mb-22'>
          <Button
            type='primary'
            onClick={handleYes}
            disabled={disabled}
            style={{ width: '100px' }}
          >
            {translate(lang, 'Yes')}
          </Button>
          <Button
            type='primary'
            danger={true}
            onClick={handleNo}
            disabled={disabled}
            style={{ width: '100px' }}
          >
            {translate(lang, 'No')}
          </Button>
          <Button
            type='default'
            onClick={handleCancel}
            disabled={disabled}
            style={{ width: '100px' }}
          >
            {translate(lang, 'Cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  ) : (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={550}
    >
      <div className='wrap text-center'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {`${lineData.farm_name} - ${lineData.line_name}`}
          </Subtitle>
        </div>
        <div className='mb-32 mt-32 d-flex justify-center'>
          <CheckboxButton
            label={translate(lang, '_requires_urgent_attention')}
            disabled={disabled}
            checked={lineData.is_urgent}
            onChange={e => toggleUrgent(e.target.checked)}
          />
        </div>
        <div
          style={{
            width: '100%',
            height: '1px',
            backgroundColor: '#78716c',
          }}
        />
        <div className='mt-32 pl-24 pb-22'>
          {lineTags?.map(x => (
            <div className='mt-7 mb-17' key={x.id}>
              <CheckboxButton
                label={`${x.name}${
                  x.is_urgent
                    ? ` (${translate(
                        lang,
                        'Required urgent attention after %s',
                        periodToStr(x.period ?? 0),
                      )})`
                    : ''
                }`}
                disabled={disabled}
                checked={selectedTags.has(x.id)}
                onChange={() => toggleTag(x.id)}
              />
            </div>
          ))}
          <div className='d-flex justify-center gap-20 mt-22'>
            <Button
              type='primary'
              disabled={disabled}
              onClick={() => setAddNew(true)}
            >
              {translate(lang, 'Create a new tag')}
            </Button>
            <Button
              type='primary'
              disabled={disabled}
              danger={true}
              onClick={() => setIsPast(true)}
            >
              {translate(lang, 'Add tag in the past')}
            </Button>
          </div>
        </div>
        <div
          style={{
            width: '100%',
            height: '1px',
            backgroundColor: '#78716c',
          }}
        />
        <div className='d-flex justify-center gap-12 mt-22 mb-16'>
          <Button type='default' onClick={handleCancel} disabled={disabled}>
            {translate(lang, 'Cancel')}
          </Button>
          <Button type='primary' onClick={handleConfirm} disabled={disabled}>
            {translate(lang, 'Confirm')}
          </Button>
          {!!onComplete && (
            <Button type='primary' disabled={disabled} onClick={onComplete}>
              {translate(lang, 'Complete')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LineTagModal;
